<template>
  <InfuseModal
    :title="t('iam.users.header.move_users')"
    @close="emit('close')"
    :disable-close="loading"
    :size="props.size"
  >
    <div class="flex flex-col gap-6">
      <InfuseField :label="t('iam.users.move_destination')">
        <InfuseSelect
          v-model="selectedDestination"
          :options="destinationOptions"
          :placeholder="t('iam.users.select_destination')"
          name="destination"
          :required="true"
        />
      </InfuseField>

      <InfuseField :label="t('iam.users.search_users')">
        <InfuseInput v-model="searchQuery" :placeholder="t('iam.users.search_users_placeholder')" name="search-users" />
      </InfuseField>

      <div class="border border-neutral-200 rounded-md max-h-96 overflow-hidden">
        <div class="p-3 border-b border-neutral-200 bg-neutral-50 flex items-center">
          <label class="flex items-center text-sm font-medium cursor-pointer">
            <InfuseCheckBox :model-value="allUsersSelected" @update:model-value="toggleSelectAll" class="mr-3" />
            {{ t('iam.users.select_all') }} ({{ filteredUsers.length }} of {{ totalElements }} {{ getUsersContext() }})
          </label>
        </div>

        <div ref="userListContainer" class="overflow-y-auto max-h-80" @scroll="onScroll">
          <div v-if="isLoading && filteredUsers.length === 0" class="p-6 text-center">
            <InfuseSpinner size="medium" />
            <p class="mt-2 text-neutral-500">{{ t('iam.users.loading_users') }}</p>
          </div>

          <div v-else-if="filteredUsers.length === 0" class="p-6 text-center text-neutral-500">
            {{ searchQuery ? t('iam.users.no_users_found') : t('iam.users.no_users_available') }}
          </div>

          <div v-else>
            <div
              v-for="user in filteredUsers"
              :key="user.id"
              class="p-3 border-b border-neutral-100 hover:bg-neutral-50 flex items-center"
            >
              <label class="flex items-center flex-1 cursor-pointer">
                <InfuseCheckBox
                  :model-value="selectedUserIds.includes(user.id)"
                  @update:model-value="(checked) => toggleUserSelection(user.id, checked)"
                  class="mr-3"
                />
                <div class="flex-1">
                  <div class="font-medium text-neutral-900">{{ user.name }}</div>
                  <div class="text-sm text-neutral-500">{{ user.email }}</div>
                  <div v-if="user.organisationName || user.tenantName" class="text-xs text-neutral-400 mt-1">
                    {{ t('iam.users.current_location') }}: {{ getCurrentLocation(user) }}
                  </div>
                </div>
              </label>
            </div>

            <!-- Loading more indicator -->
            <div v-if="isLoading && filteredUsers.length > 0" class="p-4 text-center">
              <InfuseSpinner size="small" />
              <p class="mt-1 text-sm text-neutral-500">{{ t('iam.users.loading_more') }}</p>
            </div>

            <!-- End of results indicator -->
            <div v-else-if="!hasNextPage && filteredUsers.length > 0" class="p-4 text-center text-sm text-neutral-400">
              {{ t('iam.users.end_of_results') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Selected Count -->
      <div v-if="selectedUserIds.length > 0" class="text-sm text-neutral-600 bg-neutral-50 p-3 rounded-md">
        <font-awesome-icon :icon="['fas', 'info']" class="mr-2" />
        {{ t('iam.users.selected_count', { count: selectedUserIds.length }) }}
      </div>

      <!-- Role Loss Warning -->
      <div class="text-sm text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded-md">
        <div class="flex items-start">
          <font-awesome-icon :icon="['fas', 'info-circle']" class="mr-2 mt-0.5 text-amber-600" />
          <div>
            <span class="font-bold">{{ t('iam.users.role_loss_warning_title') }}</span>
            {{ t('iam.users.role_loss_warning') }}
          </div>
        </div>
      </div>
    </div>

    <template #actions>
      <InfuseButton color="secondary" @click="emit('close')" :disabled="loading">
        {{ t('action.cancel') }}
      </InfuseButton>
      <InfuseButton :disabled="!canMove" :loading="loading" @click="onMoveUsers">
        <font-awesome-icon :icon="['fas', 'repeat']" class="mr-2" />
        {{ t('iam.users.move_users_action', { count: selectedUserIds.length }) }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { User, UserLevel, UserMoveDestinationType } from '@infuse/iam/user/type';
import {
  InfuseButton,
  InfuseCheckBox,
  InfuseField,
  InfuseInput,
  InfuseModal,
  InfuseSelect,
  InfuseSpinner
} from '@infuse/ui/components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUsers } from '@infuse/iam/user/queries';
import { useTenantUsers } from '@infuse/iam/tenant/queries';
import { useOrganisationUsers } from '@infuse/iam/organisation/queries';
import { useDebounceFn } from '@vueuse/core';
import { useDestinationOptions, parseDestination } from '@infuse/iam/composables';

import { useCurrentUserStore } from '@infuse/iam/user/store';
const currentUserStore = useCurrentUserStore();

const { t } = useI18n();

interface Props {
  loading?: boolean;
  currentLevel: UserLevel;
  currentOrganisationId?: string;
  currentTenantId?: string;
  size?: 'small' | 'medium' | 'large';
}


const props = withDefaults(defineProps<Props>(), {
  loading: false,
  size: 'large'
});

interface Emits {
  (event: 'close'): void;
  (
    event: 'move',
    data: {
      users: User[];
      destinationType: UserMoveDestinationType;
      destinationId?: string;
      targetTenantId?: string;
    }
  ): void;
}

const emit = defineEmits<Emits>();

// Use context-appropriate users query based on current level
function getUsersQuery() {
  switch (props.currentLevel) {
    case UserLevel.ORGANISATION:
      return useOrganisationUsers();
    case UserLevel.TENANT:
      return useTenantUsers();
    default:
      return useUsers();
  }
}

const {
  users,
  isLoading: isLoadingUsers,
  searchQuery: usersSearchQuery,
  fetchNextPage,
  hasNextPage,
  fetchParentId,
  totalElements
} = getUsersQuery();


const isLoading = computed(() => {
  return props.loading || isLoadingUsers.value || destinationsLoading.value;
});


// Set the parent ID based on current context
if (props.currentLevel === UserLevel.TENANT && props.currentTenantId) {
  fetchParentId.value = props.currentTenantId;
} else if (props.currentLevel === UserLevel.ORGANISATION && props.currentOrganisationId) {
  fetchParentId.value = props.currentOrganisationId;
}

// Use shared destination options composable
// If the current user is a domain user, show domain, tenants and organisations in the destination options
// If the current user is a tenant user, show tenants and organisations in the destination options
const { destinationOptions, fetchDestinations, loading: destinationsLoading } = useDestinationOptions({ excludeDomain: currentUserStore.level != UserLevel.DOMAIN, excludeTenant: ![UserLevel.DOMAIN, UserLevel.TENANT].includes(currentUserStore.level)  });

// Fetch destinations on mount
onMounted(async () => {
  await fetchDestinations();
});

// State
const searchQuery = ref('');
const selectedUserIds = ref<string[]>([]);
const selectedDestination = ref('');

// Debounced search to avoid too many API calls
const debouncedSearch = useDebounceFn((query: string) => {
  usersSearchQuery.value = query || undefined;
}, 300);

// Watch search input and trigger debounced search
watch(searchQuery, (newQuery) => {
  debouncedSearch(newQuery);
});

// Template ref for scroll container
const userListContainer = ref<HTMLElement>();

// Infinite scroll handler
function onScroll(event: Event) {
  const container = event.target as HTMLElement;
  const scrollThreshold = 50; // Trigger when 50px from bottom

  if (
    container.scrollHeight - container.scrollTop - container.clientHeight < scrollThreshold &&
    hasNextPage.value &&
    !isLoading.value
  ) {
    fetchNextPage();
  }
}

const filteredUsers = computed(() => {
  return users.value;
});

const allUsersSelected = computed(() => {
  return filteredUsers.value.length > 0 && filteredUsers.value.every((user) => selectedUserIds.value.includes(user.id));
});

const canMove = computed(() => {
  return selectedUserIds.value.length > 0 && selectedDestination.value !== '';
});

function toggleSelectAll(checked: boolean) {
  if (checked) {
    // Select all filtered users
    const filteredIds = filteredUsers.value.map((user) => user.id);
    selectedUserIds.value = [...new Set([...selectedUserIds.value, ...filteredIds])];
  } else {
    // Deselect all filtered users
    selectedUserIds.value = selectedUserIds.value.filter((id) => !filteredUsers.value.some((user) => user.id === id));
  }
}

function toggleUserSelection(userId: string, checked: boolean) {
  if (checked) {
    if (!selectedUserIds.value.includes(userId)) {
      selectedUserIds.value.push(userId);
    }
  } else {
    const index = selectedUserIds.value.indexOf(userId);
    if (index > -1) {
      selectedUserIds.value.splice(index, 1);
    }
  }
}

function getCurrentLocation(user: User): string {
  if (user.organisationName) return user.organisationName;
  if (user.tenantName) return user.tenantName;
  return 'Domain';
}

function getUsersContext(): string {
  switch (props.currentLevel) {
    case UserLevel.ORGANISATION:
      return t('iam.users.context_organisation_users');
    case UserLevel.TENANT:
      return t('iam.users.context_tenant_users');
    default:
      return t('iam.users.context_users');
  }
}

function onMoveUsers() {
  if (!canMove.value) return;

  try {
    const parsed = parseDestination(selectedDestination.value);
    const selectedUsers = filteredUsers.value.filter((user) => selectedUserIds.value.includes(user.id));

    // Convert UserLevel to UserMoveDestinationType (they have the same values)
    const destinationType = parsed.destinationType as UserMoveDestinationType;

    emit('move', {
      users: selectedUsers,
      destinationType,
      destinationId: parsed.destinationId,
      targetTenantId: parsed.targetTenantId
    });
  } catch (error) {
    console.error('Failed to parse destination:', error);
  }
}
</script>
